
























































































































































































































































































import { Component, Prop, Mixins, Inject } from "vue-property-decorator";
import { mapState } from "vuex";
import _ from "@/boot/lodash";
import { ValidationObserver } from "vee-validate";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import HasValidation from "@/mixins/HasValidation";
import BrandMixin from "@/mixins/brandMixin";
import CurrenciesStore from "@/mixins/constants/currenciesStore";
import BillingCyclesStore from "@/mixins/constants/billingCyclesStore";

// Enums/Data
import { BASE_URL } from "@/store/modules/api";
import { DataModules } from "@/store/modules/data";
import { PaymentPeriodsMap } from "@/data/product";
import { ProductTypes, TrialEndActionTypes } from "@/data/enums/products";
import { ConfirmModalModes } from "@/data/enums";
import { Inheritance } from "@/data/enums/priceLists";
import { BrandConfigKeys } from "@/data/constants";

// Types
import { IState } from "@/store";
import {
  IProduct,
  IProductBillingFullForm,
  IProductPriceRecalculation,
  IPricelist,
  IProductPricesIssueMessages,
  IProductPricesIssueMessage,
  IProductPrice,
  IProductPriceMatrix,
  IProductPriceMatrixShort,
  IProductPricingForm,
  IProductBillingForm,
  IProductTrialForm
} from "@/models/products";
import { IProductProviderData } from "@/models/providers/products";
import { ICurrency } from "@/models/constants";

@Component({
  components: {
    "product-price-matrix": () =>
      import(
        "@/components/app/admin/catalogue/products/billing/productPriceMatrix.vue"
      ),
    "price-issue-messages": () =>
      import(
        "@/components/app/admin/catalogue/products/billing/priceIssueMessages.vue"
      ),
    "tax-template-select": () =>
      import("@/components/app/global/tax/templates/taxTemplatesSelect.vue"),
    "allow-affiliate-checkbox": () =>
      import(
        "@/components/app/admin/catalogue/products/allowAffiliateCheckbox.vue"
      ),
    "trial-form": () =>
      import("@/components/app/admin/catalogue/products/billing/trialForm.vue"),
    "revenue-recognition-field": () =>
      import(
        "@/components/app/admin/catalogue/products/billing/revenueRecognitionField.vue"
      )
  },
  computed: {
    ...mapState({
      productScope(state: IState, getters) {
        return getters[`data/${DataModules.CATALOGUE}/scope`](this.productId);
      },
      product(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: DataModules.CATALOGUE,
          scope: this.productScope
        });
      },
      priceLists(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: DataModules.PRICELISTS,
          scope: this.priceListsScope
        });
      },
      priceListsScope(state: IState, getters) {
        return getters[`data/${DataModules.PRICELISTS}/priceListsScope`](
          this.brandId
        );
      },
      productWarningMessages(state: IState, getters) {
        return getters[`data/${DataModules.CATALOGUE}/warningMessages`](
          this.productId
        );
      }
    })
  }
})
export default class EditProductBilling extends Mixins(
  RequestPromises,
  HasValidation,
  CurrenciesStore,
  BillingCyclesStore,
  BrandMixin
) {
  public declare $refs: {
    productPricesMatrix: HTMLFormElement;
    observer: InstanceType<typeof ValidationObserver>;
    trialForm: HTMLFormElement;
  };

  // Getters

  public product!: IProduct;
  public productScope!: string;
  public priceLists!: IPricelist[];
  public priceListsScope!: string;
  public productWarningMessages!: IProductPricesIssueMessages[];

  // Inject

  @Inject({ from: "productProviderData" })
  public readonly $productProviderData!: () => IProductProviderData;

  // Props

  @Prop({ required: true })
  public productId!: IProduct["id"];

  @Prop({ required: true })
  public productTypes!: ProductTypes[];

  // Data

  public isLoading = true;
  public isReloading = false;
  public requestError = false;
  public isProcessing = false;

  public pricingForm = {} as IProductPricingForm;
  public billingForm = {} as IProductBillingForm;
  public trialForm = {} as IProductTrialForm;

  public initialPricingForm = {} as IProductPricingForm;
  public initialBillingForm = {} as IProductBillingForm;
  public initialTrialForm = {} as IProductTrialForm;

  public get form(): IProductBillingFullForm {
    return {
      ...this.billingForm,
      ...this.pricingForm,
      ...(this.showTrialSettings ? this.trialForm : {})
    } as IProductBillingFullForm;
  }

  public get initialForm(): IProductBillingFullForm {
    return {
      ...this.initialBillingForm,
      ...this.initialPricingForm,
      ...(this.showTrialSettings ? this.initialTrialForm : {})
    } as IProductBillingFullForm;
  }

  public get trialFormData() {
    return {
      ..._.pick(this.product, [
        "trial_force",
        "trial_supported",
        "trial_duration",
        "trial_end_action",
        "trial_pre_expire_notification"
      ]),
      ...(_.isEmpty(this.product.trial_migration_rule)
        ? {}
        : {
            trial_migration_rule: _.pick(this.product.trial_migration_rule, [
              "new_product_id",
              "new_product_billing_cycle_months"
            ])
          })
    };
  }

  public get trialNewProduct() {
    return _.get(this.product, "trial_migration_rule.new_product", null);
  }

  public initiateTrialForm() {
    this.$nextTick(() => {
      this.initialTrialForm = _.cloneDeep(
        this.$refs.trialForm?.initialForm || {}
      );
    });
  }

  public get trialFormInitiated() {
    if (!this.isMainProduct) return true;

    return !this.recurringProduct || !_.isEmpty(this.initialTrialForm);
  }

  public get trialRuleIsValid() {
    if (this.form.trial_end_action !== TrialEndActionTypes.MIGRATE) return true;

    return (
      !!this.form.trial_migration_rule?.new_product_id &&
      !!this.form.trial_migration_rule?.new_product_billing_cycle_months
    );
  }

  public get isFormChanged() {
    return (
      this.trialFormInitiated &&
      (!_.isEqual(this.form, this.initialForm) ||
        this.recurringProduct !==
          _.get(this.product, "billing_cycle_months", 0) > 0) &&
      this.trialRuleIsValid
    );
  }

  public get showTrialSettings() {
    return this.recurringProduct && this.isMainProduct;
  }

  public get isTrialCancelProduct() {
    return this.trialForm?.trial_end_action === TrialEndActionTypes.CANCEL;
  }

  public get accountingRevenueRecognitionBV() {
    return this.$store.getters["brand/config"][
      BrandConfigKeys.ACCOUNTING_REVENUE_RECOGNITION
    ];
  }

  public recurringProduct = false;

  public recalculation = {} as IProductPriceRecalculation;
  public paymentPeriods = PaymentPeriodsMap();

  public activePriceListId: IPricelist["id"] = "";
  public usedCurrencies: ICurrency[] = [];
  public savedUsedTerms: number[] = [];
  public priceErrors: any = {};
  public productPricesMatrixUUID = "";
  public changedTerms: {
    oldTerm: number;
    newTerm: number;
  }[] = [];

  public useSelectedProductPrices = false;
  public selectedProductPrices: IProductPriceMatrixShort[] = [];

  // Life-cycle methods

  public async created() {
    if (!this.isContentAvailable) return;

    this.loadData();
  }

  public beforeDestroy() {
    this.$store.dispatch("data/bin", {
      storeModule: DataModules.CATALOGUE,
      scope: this.productScope,
      vm: this
    });

    this.$store.dispatch("data/binList", {
      storeModule: DataModules.PRICELISTS,
      scope: this.priceListsScope,
      vm: this
    });
  }

  // Methods

  public async loadData() {
    try {
      await Promise.all([
        this.getPriceLists(),
        this.getProductData(this.isReloading),
        this.getBrandConfig(),
        this.getCurrencies(),
        this.getBillingCycles()
      ]);

      this.activePriceListId = this.brandPriceListId;
      this.updateRecurringProduct();
      this.initialiseForm();
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public getProductData(ignoreStored = false) {
    return this.$store.dispatch(`data/${DataModules.CATALOGUE}/get`, {
      id: this.productId,
      scope: this.productScope,
      vm: this,
      ignoreStored
    });
  }

  public getPriceLists() {
    return this.$store.dispatch(`data/${DataModules.PRICELISTS}/list`, {
      scope: this.priceListsScope,
      vm: this,
      params: { limit: 0, with: "parents" }
    });
  }

  public getBrandConfig() {
    return this.$store.dispatch("brand/getConfig", {
      keys: [BrandConfigKeys.ACCOUNTING_REVENUE_RECOGNITION],
      brandId: this.$store.state.auth.admin.brandId
    });
  }

  public initialiseForm() {
    // Pricing form
    this.$set(this.pricingForm, "prices", []);

    // Billing form
    this.$set(
      this,
      "billingForm",
      _.pick(
        this.product,
        _.concat(
          [
            "accounting_revenue_recognition",
            "additional_currency_recalculation",
            "default_payment_period",
            "tax_template_id"
          ],
          this.isMainProduct ? ["allow_affiliate"] : []
        )
      )
    );
    this.initialBillingForm = _.cloneDeep(this.billingForm);

    if (this.$refs.trialForm) this.$refs.trialForm.revertForm();

    // Rest of the data
    this.useSelectedProductPrices = false;
    this.selectedProductPrices = [];
    this.updateRecalculation();
    this.initiateMatrixMemory();
  }

  public changeRecalculation(newRecalculation) {
    _.set(
      this.billingForm,
      "additional_currency_recalculation",
      newRecalculation ? newRecalculation.value : 0
    );

    if (!newRecalculation) {
      this.recalculation = this.multicurrencyOptions[0];
    }
  }

  public get productPricingKey() {
    return _.map(
      _.get(this.product, "prices", []),
      price =>
        `${price.pricelist_id}${price.currency_id}${price.billing_cycle_months}${price.price}`
    ).join("_");
  }

  public get isStaged() {
    return this.$productProviderData().isStaged;
  }

  public get defaultPricelistId() {
    return this.brand.pricelist_id;
  }

  public async revertToInitialState() {
    this.pricingForm = _.cloneDeep(this.initialPricingForm);
    this.billingForm = _.cloneDeep(this.initialBillingForm);
    if (this.$refs.trialForm) this.$refs.trialForm.revertForm();

    this.useSelectedProductPrices = false;
    this.selectedProductPrices = [];
    this.updateRecurringProduct();
    this.updateRecalculation();
    this.initiateMatrixMemory();
  }

  public initiateMatrixMemory() {
    this.usedCurrencies = _.uniqBy(
      _.compact(
        _.concat(
          [this.brandCurrency],
          this.productPrices.length
            ? _.map(
                _.uniqBy(this.productPrices, "currency_id"),
                price =>
                  _.find(this.currencies, [
                    "id",
                    price["currency_id"]
                  ]) as ICurrency
              )
            : this.brandCurrencies
        )
      ),
      "id"
    );
    this.savedUsedTerms = this.recurringProduct
      ? _.map(
          _.uniqBy(this.productPrices, "billing_cycle_months"),
          price => price["billing_cycle_months"]
        )
      : [0];
    this.initialisePriceErrors();
    this.changedTerms = [];
    this.productPricesMatrixUUID = this.$uuid();
  }

  public initialisePriceErrors() {
    _.forEach(this.priceLists, priceList => {
      const pricelistErrors = {};

      _.forEach(this.usedTerms, usedTerm => {
        _.forEach(this.usedCurrencies, (currency: ICurrency) => {
          if (
            currency.id === this.brandCurrencyId &&
            _.some(
              this.productPrices,
              productPrice =>
                productPrice.pricelist_id === priceList.id &&
                productPrice.billing_cycle_months === usedTerm
            ) &&
            !_.some(this.productPrices, (productPrice: IProductPrice) => {
              return (
                productPrice.pricelist_id === priceList.id &&
                productPrice.billing_cycle_months === usedTerm &&
                productPrice.currency_id === currency.id &&
                productPrice.price !== null
              );
            })
          ) {
            pricelistErrors[`${currency.code}_${usedTerm}`] = [
              this.$t("_sentence.amount_field_required")
            ];
          }
        });
      });

      _.forEach(
        _.filter(_.get(this.productWarningMessages, "data", []), [
          "pricelist_id",
          priceList.id
        ]),
        error => {
          const code = this.getCurrencyCode(error.currency_id);
          const term = error.billing_cycle_months;
          const errorKey = `${code}_${term}`;
          const errorMessage = _.get(
            this.productWarningMessages,
            "message",
            ""
          );

          if (_.get(pricelistErrors, errorKey, []).length) {
            pricelistErrors[errorKey].push(errorMessage);
          } else {
            pricelistErrors[errorKey] = [errorMessage];
          }
        }
      );

      this.$set(this.priceErrors, priceList.id, pricelistErrors);
    });
  }

  public updateRecurringProduct() {
    this.$set(
      this,
      "recurringProduct",
      _.get(this.product, "billing_cycle_months", 0) > 0
    );
  }

  public updateRecalculation() {
    this.recalculation = _.find(
      this.multicurrencyOptions,
      option =>
        option.value ===
        _.get(this.billingForm, "additional_currency_recalculation", 0)
    ) as IProductPriceRecalculation;
  }

  public autoPopulate() {
    this.$store.dispatch(
      `data/${DataModules.CATALOGUE}/openAutoPopulateModal`,
      {
        props: {
          prices: this.pricingForm.prices,
          activePriceListId: this.activePriceListId,
          usedCurrencies: this.usedCurrencies
        },
        events: {
          success: resultForm => {
            this.usedCurrencies = _.uniqBy(
              _.concat(
                this.usedCurrencies,
                _.map(
                  _.uniqBy(resultForm.prices, "currency_id"),
                  price =>
                    _.find(this.currencies, [
                      "id",
                      price["currency_id"]
                    ]) as ICurrency
                )
              ),
              "id"
            );

            this.savedUsedTerms = _.uniq(
              _.concat(this.savedUsedTerms, resultForm.selectedTerms)
            );

            _.forEach(resultForm.prices, newPrice => {
              _.forEach(resultForm.selectedTerms, (selectedTerm: number) => {
                const priceIndex = _.findIndex(
                  this.pricingForm.prices,
                  formPrice =>
                    formPrice.pricelist_id === this.activePriceListId &&
                    formPrice.billing_cycle_months === selectedTerm &&
                    formPrice.currency_id === newPrice.currency_id
                );
                const calculatedMonthPrice =
                  resultForm.usedTerm === 1
                    ? selectedTerm * newPrice.price
                    : selectedTerm < 12
                    ? Math.round(
                        (selectedTerm * (newPrice.price / 12) +
                          Number.EPSILON) *
                          100
                      ) / 100
                    : (selectedTerm / 12) * newPrice.price;

                if (priceIndex !== -1) {
                  if (!resultForm.overwrite) return;
                  this.pricingForm.prices[
                    priceIndex
                  ].price = calculatedMonthPrice;
                } else {
                  this.pricingForm.prices.push({
                    ..._.pick(newPrice, [
                      "pricelist_id",
                      "currency_id",
                      "available_for_clients"
                    ]),
                    billing_cycle_months: selectedTerm,
                    price: calculatedMonthPrice
                  } as IProductPriceMatrix);
                }
              });

              const priceIndex = _.findIndex(
                this.pricingForm.prices,
                formPrice =>
                  formPrice.pricelist_id === this.activePriceListId &&
                  formPrice.billing_cycle_months ===
                    newPrice.billing_cycle_months &&
                  formPrice.currency_id === newPrice.currency_id
              );

              if (priceIndex !== -1) {
                if (!resultForm.overwrite) return;
                this.pricingForm.prices[priceIndex].price = newPrice.price;
                this.pricingForm.prices[priceIndex].available_for_clients =
                  newPrice.available_for_clients;
              } else {
                this.pricingForm.prices.push(newPrice as IProductPriceMatrix);
              }
            });

            this.selectedProductPrices = _.map(
              this.pricingForm.prices,
              (price: IProductPriceMatrix) => {
                return {
                  ...price,
                  currency_code: _.get(
                    _.find(this.currencies, ["id", price.currency_id]),
                    "code"
                  ),
                  own_price: true
                };
              }
            ) as IProductPriceMatrixShort[];

            this.useSelectedProductPrices = true;
            this.productPricesMatrixUUID = this.$uuid();
          }
        }
      }
    );
  }

  public autoPopulateFromProduct() {
    this.$store.dispatch(
      `data/${DataModules.CATALOGUE}/openSelectProductsModal`,
      {
        props: {
          productTypes: [
            ProductTypes.SINGLE_PRODUCT,
            ProductTypes.PRODUCT_BUNDLE
          ],
          selectedProducts: [],
          disabledProducts: [this.productId],
          isMultiselect: false
        },
        events: {
          success: async results => {
            const selectedProductId = _.get(results, "[0].id");

            try {
              this.isProcessing = true;

              const selectedProduct = await this.$store.dispatch(
                `data/${DataModules.CATALOGUE}/get`,
                {
                  id: selectedProductId,
                  returnData: true,
                  storeData: false,
                  vm: this
                }
              );
              this.selectedProductPrices = _.get(selectedProduct, "prices", []);
              this.$set(
                this,
                "recurringProduct",
                _.get(selectedProduct, "billing_cycle_months", 0) > 0
              );
              this.useSelectedProductPrices = true;
              this.initiateMatrixMemory();
            } catch (error) {
              this.$store.dispatch("api/handleError", error);
            } finally {
              this.isProcessing = false;
            }
          }
        }
      }
    );
  }

  public async clearPricing() {
    if (!this.canManagePrices) return;

    const confirm = await this.$store.dispatch("ui/open/confirmModal", {
      config: {
        props: {
          autoClose: false,
          mode: ConfirmModalModes.BUTTON,
          confirmButtonText: this.$t("_action.delete"),
          message: this.$t("_sentence.clear_pricing_confirmation")
        },
        events: {
          confirmed: async () => {
            try {
              this.isProcessing = true;

              await Promise.all(
                _.map(this.priceLists, ({ id }) =>
                  this.updatePriceListPrices(id, [])
                )
              );

              await this.getProductData(true);

              this.initialiseForm();

              this.$store.dispatch("toast/show", {
                message: this.$t("_.product_billing_successfully_saved")
              });
            } catch (error) {
              this.$store.dispatch("api/handleError", error);
            } finally {
              this.isProcessing = false;
            }

            confirm.close();
          }
        }
      }
    });
  }

  public initiateFormPrices() {
    this.savedUsedTerms = this.usedTerms;
    if (!this.useSelectedProductPrices) {
      this.initialPricingForm = _.cloneDeep(this.pricingForm);
    }
  }

  public get productPrices() {
    if (this.useSelectedProductPrices) {
      return this.selectedProductPrices;
    }

    return _.get(this.product, "prices", []);
  }

  public get activePricelist() {
    return _.find(this.priceLists, ["id", this.activePriceListId]);
  }

  public get showCancelOption() {
    if (!this.recurringProduct) return false;
    return (
      this.productTypes.includes(ProductTypes.SINGLE_PRODUCT) ||
      this.productTypes.includes(ProductTypes.PRODUCT_BUNDLE)
    );
  }

  public async addCurrency() {
    this.$store.dispatch(
      `data/${DataModules.CATALOGUE_CATEGORIES}/openSelectProductCurrencyModal`,
      {
        props: {
          currencies: this.currencies,
          usedCurrenciesIds: _.map(
            this.usedCurrencies,
            (usedCurrency: ICurrency) => usedCurrency.id
          ),
          isMultiple: true
        },
        events: {
          success: results => {
            this.addNewCurrencies(results);
          }
        }
      }
    );
  }

  public async addNewCurrencies(currenciesIds) {
    _.forEach(currenciesIds, currencyId => {
      this.usedCurrencies.push(
        _.find(this.currencies, ["id", currencyId]) as ICurrency
      );
    });

    setTimeout(() => {
      _.forEach(this.priceLists, priceList => {
        const usedPrices = this.getPriceListPrices(
          priceList.id
        ) as IProductPrice[];

        if (
          !usedPrices.length ||
          priceList.inheritance === Inheritance.INHERITED
        )
          return;

        _.forEach(this.usedTerms, usedTerm => {
          _.forEach(this.usedCurrencies, (currency: ICurrency) => {
            if (currency.id !== this.brandCurrencyId) return;

            const currencyCode = this.getCurrencyCode(currency.id);
            const usedTermErrors = _.get(
              this.priceErrors[priceList.id],
              `${currencyCode}_${usedTerm}`,
              []
            );
            if (
              _.some(
                usedPrices,
                productPrice =>
                  productPrice.pricelist_id === priceList.id &&
                  productPrice.billing_cycle_months === usedTerm
              ) &&
              !_.some(usedPrices, (productPrice: IProductPrice) => {
                return (
                  productPrice.pricelist_id === priceList.id &&
                  productPrice.billing_cycle_months === usedTerm &&
                  productPrice.currency_id === this.brandCurrencyId &&
                  productPrice.price !== null
                );
              })
            ) {
              if (
                usedTermErrors.length &&
                _.some(
                  usedTermErrors,
                  message => message.indexOf("is required") !== -1
                )
              )
                return;

              usedTermErrors.push(this.$t("_sentence.amount_field_required"));

              this.$set(
                this.priceErrors[priceList.id],
                `${currencyCode}_${usedTerm}`,
                usedTermErrors
              );
            }
          });
        });
      });
    }, 50);
  }

  public get parentPriceListName() {
    return _.get(
      _.find(this.priceLists, ["id", this.activePriceListId]),
      "parents[0]name",
      ""
    );
  }

  public showRemoveCurrencyConfirmation(currencyId: ICurrency["id"]) {
    return _.some(this.productPrices, priceObj => {
      return (
        priceObj.currency_id === currencyId &&
        priceObj.price !== null &&
        priceObj.own_price &&
        priceObj.pricelist_id !== this.brandPriceListId
      );
    });
  }

  public async removeCurrency(currencyId: ICurrency["id"]) {
    if (!this.showRemoveCurrencyConfirmation(currencyId)) {
      this.filterOutCurrency(currencyId);
      return;
    }

    this.$store.dispatch("ui/open/confirmModal", {
      config: {
        props: {
          autoClose: true,
          mode: ConfirmModalModes.BUTTON,
          confirmButtonText: this.$t("_action.delete"),
          message: this.$t("_sentence.delete_currency_not_empty")
        },
        events: {
          confirmed: async () => {
            this.filterOutCurrency(currencyId);
          }
        }
      }
    });
  }

  public filterOutCurrency(currencyId: ICurrency["id"]) {
    this.usedCurrencies = _.filter(
      this.usedCurrencies,
      currency => currency.id !== currencyId
    );

    setTimeout(() => {
      _.forEach(this.priceErrors, (pricelistErrors, errorPricelistId) => {
        const usedPrices = this.getPriceListPrices(
          errorPricelistId
        ) as IProductPrice[];

        _.forEach(pricelistErrors, (priceErrorValue, priceErrorKey) => {
          const [code, term] = priceErrorKey.split("_");

          if (
            code === this.brandCurrencyCode &&
            !_.some(
              usedPrices,
              usedPrice =>
                usedPrice.billing_cycle_months === _.toNumber(term) &&
                code !== this.brandCurrencyCode
            ) &&
            _.some(
              priceErrorValue,
              message => message.indexOf("is required") !== -1
            )
          ) {
            this.$set(
              this.priceErrors[errorPricelistId],
              priceErrorKey,
              _.filter(
                priceErrorValue,
                message => message.indexOf("is required") === -1
              )
            );
          }
        });
      });
    }, 50); // To avoid racing conditions
  }

  public addTerm(termId: number) {
    this.savedUsedTerms.push(termId);
  }

  public removeTerm(term: number) {
    if (!this.showRemoveTermConfirmation(term)) {
      this.filterOutTerm(term);
      return;
    }

    this.$store.dispatch("ui/open/confirmModal", {
      config: {
        props: {
          autoClose: true,
          mode: ConfirmModalModes.BUTTON,
          confirmButtonText: this.$t("_action.delete"),
          message: this.$t("_sentence.delete_term_not_empty")
        },
        events: {
          confirmed: () => {
            this.filterOutTerm(term);
          }
        }
      }
    });
  }

  public filterOutTerm(term: number) {
    this.savedUsedTerms = _.filter(
      this.savedUsedTerms,
      savedUsedTerm => savedUsedTerm !== term
    );
  }

  public updateTerm(oldTerm, newTerm) {
    if (
      !this.showRemoveTermConfirmation(oldTerm) ||
      _.find(
        this.changedTerms,
        changedTerm =>
          changedTerm.newTerm === oldTerm && changedTerm.oldTerm === newTerm
      )
    ) {
      this.changeTerm(oldTerm, newTerm);
      return;
    }

    this.$store.dispatch("ui/open/confirmModal", {
      config: {
        props: {
          autoClose: true,
          mode: ConfirmModalModes.BUTTON,
          title: this.$t("_.change_term"),
          confirmButtonText: this.$t("_action.change"),
          message: this.$t("_sentence.change_term_not_empty")
        },
        events: {
          confirmed: () => {
            this.changeTerm(oldTerm, newTerm);
          }
        }
      }
    });
  }

  public changeTerm(oldTerm: number, newTerm: number) {
    if (
      _.find(
        this.changedTerms,
        changedTerm =>
          changedTerm.newTerm === oldTerm && changedTerm.oldTerm === newTerm
      )
    ) {
      this.changedTerms = _.filter(
        this.changedTerms,
        changedTerm =>
          !(changedTerm.newTerm === oldTerm && changedTerm.oldTerm === newTerm)
      );
    }

    this.$refs.productPricesMatrix.changeTermPrices(oldTerm, newTerm);

    _.forEach(this.priceErrors, (pricelistErrors, errorPricelistId) => {
      _.forEach(pricelistErrors, (priceErrorValue, priceErrorKey) => {
        const [code, term] = priceErrorKey.split("_");

        if (term === oldTerm.toString()) {
          delete this.priceErrors[errorPricelistId][priceErrorKey];
          this.$set(
            this.priceErrors[errorPricelistId],
            `${code}_${newTerm}`,
            priceErrorValue
          );
        }
      });
    });

    this.changedTerms.push({ oldTerm, newTerm });

    this.filterOutTerm(oldTerm);
    this.savedUsedTerms.push(newTerm);
  }

  public showRemoveTermConfirmation(term: number) {
    return _.some(this.productPrices, priceObj => {
      return (
        priceObj.billing_cycle_months === term &&
        priceObj.price !== null &&
        priceObj.own_price &&
        priceObj.pricelist_id !== this.brandPriceListId
      );
    });
  }

  public get productPricesMatrixKey() {
    return `${this.activePriceListId}_${this.productPricesMatrixUUID}`;
  }

  public get multicurrencyOptions() {
    return [
      {
        leftLabel: this.$t("_.omit_product") as string,
        desc: this.$t("_sentence.omit_product_desc") as string,
        value: 0
      },
      {
        leftLabel: this.$t("_.make_available") as string,
        desc: this.$t("_sentence.make_available_desc", {
          currency: this.brandCurrencyCode
        }) as string,
        value: 1
      },
      {
        leftLabel: this.$t("_.inherit_brand_default_value", {
          value: this.$t("_.omit_product")
        }) as string,
        value: 2
      }
    ];
  }

  public get usedCurrenciesIds() {
    return _.sortBy(
      _.map(this.usedCurrencies, (usedCurrency: ICurrency) => usedCurrency.id)
    );
  }

  public get priceListsNeedsUpdate() {
    if (!this.productPrices.length) {
      return [this.activePriceListId];
    }

    if (
      _.get(this.product, "billing_cycle_months") > 0 !==
      this.recurringProduct
    ) {
      return _.map(
        _.filter(_.uniqBy(this.productPrices, "pricelist_id"), [
          "own_price",
          true
        ]),
        price => price.pricelist_id
      );
    }

    const originalCurrencies = _.sortBy(
      _.map(
        _.uniqBy(this.productPrices, "currency_id"),
        productPrice => productPrice.currency_id
      )
    );

    const originalTerms =
      _.get(this.product, "billing_cycle_months") > 0
        ? _.sortBy(
            _.map(
              _.uniqBy(this.productPrices, "billing_cycle_months"),
              price => price["billing_cycle_months"]
            )
          )
        : [0];

    return _.uniq(
      _.concat(
        [this.activePriceListId],
        _.map(
          _.uniqBy(
            _.filter(this.productPrices, productPrice => {
              return (
                productPrice.pricelist_id !== this.activePriceListId &&
                productPrice.own_price &&
                (_.difference(
                  originalCurrencies,
                  this.usedCurrenciesIds
                ).includes(productPrice.currency_id) ||
                  _.difference(originalTerms, this.usedTerms).includes(
                    productPrice.billing_cycle_months
                  ))
              );
            }),
            "pricelist_id"
          ),
          price => price.pricelist_id
        )
      )
    );
  }

  public async validateBeforeSubmit() {
    if (!this.canManagePrices) return;

    try {
      if (!(await this.validate())) {
        throw this.$t("_sentence.form_is_not_valid");
      }

      this.isProcessing = true;

      await this.updateProductData();
      await this.getProductData(true);

      this.updateRecalculation();

      if (
        this.initialForm.additional_currency_recalculation !==
        this.billingForm.additional_currency_recalculation
      ) {
        this.$set(
          this.initialForm,
          "additional_currency_recalculation",
          this.billingForm.additional_currency_recalculation
        );
      }

      await Promise.all(
        _.map(this.priceListsNeedsUpdate, priceListId =>
          this.updatePriceListPrices(priceListId)
        )
      );

      await this.getProductData(true);

      this.initialiseForm();

      this.$store.dispatch("toast/show", {
        message: this.$t("_.product_billing_successfully_saved")
      });
    } catch (err) {
      const error = err ?? (new Error() as any);
      const errorUrl = _.get(error, "response.config.url", "");

      if (errorUrl.includes(`${BASE_URL}api/admin/pricelists/`)) {
        const errorSentData = _.get(
          JSON.parse(_.get(error, "response.config.data", "{}")),
          "prices",
          []
        );

        const is422Error = _.get(error, "response.status") === 422;

        const errorData = is422Error
          ? _.filter(
              _.get(error, "response.data.error.data", []),
              (errorValue, errorKey) => {
                return errorKey.includes(".price");
              }
            )
          : _.get(error, "response.data.error.data.prices", []);

        const newErrorData = {};

        _.forEach(errorData, (errorValue, errorKey) => {
          const errorPrice = is422Error
            ? errorSentData[
                errorKey
                  .toString()
                  .replace("prices.", "")
                  .replace(".price", "")
              ]
            : errorData[0];

          const newKey = `${this.getCurrencyCode(errorPrice.currency_id)}_${
            errorPrice.billing_cycle_months
          }`;
          const newValue = [
            is422Error
              ? errorValue[0]
              : _.get(error, "response.data.error.message")
          ];

          this.$set(newErrorData, newKey, newValue);
          this.$set(this.priceErrors, newKey, newValue);
        });

        _.set(error, "response.data.error.data", newErrorData);

        if (_.get(error, "response.status") === 409) {
          _.set(error, "response.data.error.code", 422);
          _.set(error, "response.status", 422);
        }
      }

      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.isProcessing = false;
    }
  }

  public updatePriceListPrices(priceListId, prices: null | any[] = null) {
    return this.$store.dispatch(
      `data/${DataModules.PRICELISTS}/updateProductPrices`,
      {
        id: this.productId,
        pricelistId: priceListId,
        data: {
          prices: _.isNull(prices)
            ? this.getPriceListPrices(priceListId)
            : prices
        }
      }
    );
  }

  public getPriceListPrices(priceListId) {
    if (this.activePriceListId !== priceListId) {
      if (
        _.get(this.product, "billing_cycle_months") > 0 !==
        this.recurringProduct
      ) {
        return [];
      }

      const oldTerms = _.map(
        this.changedTerms,
        changedTerm => changedTerm.oldTerm
      );

      let priceListPrices = _.map(
        _.filter(this.productPrices, priceObj => {
          return (
            priceObj.own_price &&
            priceObj.pricelist_id === priceListId &&
            this.usedCurrenciesIds.includes(priceObj.currency_id) &&
            (this.usedTerms.includes(priceObj.billing_cycle_months) ||
              oldTerms.includes(priceObj.billing_cycle_months))
          );
        }),
        price =>
          _.pick(price, [
            "currency_id",
            "billing_cycle_months",
            "price",
            "pricelist_id"
          ])
      );

      if (this.changedTerms.length) {
        _.forEach(this.changedTerms, changedTerm => {
          priceListPrices = _.map(priceListPrices, priceListPrice => {
            if (priceListPrice.billing_cycle_months === changedTerm.oldTerm) {
              priceListPrice.billing_cycle_months = changedTerm.newTerm;
            }
            return priceListPrice;
          });
        });
      }

      return priceListPrices;
    }

    if (this.isTrialCancelProduct && _.isEmpty(this.pricingForm.prices)) {
      return [
        {
          billing_cycle_months: 1,
          currency_id: this.brandCurrencyId,
          price: 0,
          pricelist_id: this.defaultPricelistId
        }
      ];
    }

    return this.pricingForm.prices;
  }

  public get hasPriceValidationErrors() {
    return _.some(
      _.filter(
        this.priceErrors[this.activePriceListId],
        (priceErrorValue, priceErrorKey) => {
          const [code, term] = priceErrorKey.split("_");
          return (
            this.usedTerms.includes(_.toNumber(term)) &&
            _.map(this.usedCurrencies, currency => currency["code"]).includes(
              code
            )
          );
        }
      ),
      errorValue => {
        return !!errorValue.length;
      }
    );
  }

  public updateProductData() {
    return this.$store.dispatch(`data/${DataModules.CATALOGUE}/update`, {
      id: this.productId,
      scope: this.productScope,
      data: this.getNewValues()
    });
  }

  public getNewValues() {
    const newValues: any = _.omitBy(this.form, (v, k) => {
      return this.initialForm[k] === v;
    });

    if (this.isTrialCancelProduct && _.isEmpty(this.pricingForm.prices)) {
      newValues["additional_currency_recalculation"] = 1;
    }

    delete newValues.prices;
    newValues["billing_cycle_months"] = this.recurringProduct ? 1 : 0;

    return newValues;
  }

  public get usedTerms() {
    return this.recurringProduct
      ? _.filter(this.savedUsedTerms, term => term > 0)
      : [0];
  }

  public get isMainProduct() {
    return (
      this.productTypes.includes(ProductTypes.SINGLE_PRODUCT) ||
      this.productTypes.includes(ProductTypes.PRODUCT_BUNDLE)
    );
  }

  public get issueMessages() {
    let data: IProductPricesIssueMessages[] = [];

    // Fill all missing price warnings
    _.forEach(this.usedTerms, usedTerm => {
      _.forEach(this.usedCurrencies, (currency: ICurrency) => {
        if (
          currency.id !== this.brandCurrencyId &&
          !_.some(
            this.activePriceListId === this.brandPriceListId
              ? this.pricingForm.prices
              : this.productPrices,
            (price: IProductPrice) => {
              return (
                price.pricelist_id === this.brandPriceListId &&
                price.billing_cycle_months === usedTerm &&
                price.currency_id === currency.id &&
                price.price !== null
              );
            }
          )
        ) {
          data = this.addIssue(
            data,
            {
              billing_cycle_months: usedTerm,
              currency_id: currency.id,
              message: this.$t("_sentence.missing_prices_msg").toString(),
              isError: false
            },
            this.brandPriceListId
          );
        }
      });
    });

    // Fill validation errors (FE and 422s)
    _.forEach(this.priceErrors, (pricelistErrors, errorPricelistId) => {
      _.forEach(pricelistErrors, (priceErrorValue, priceErrorKey) => {
        const [code, term] = priceErrorKey.split("_");

        if (
          this.usedTerms.includes(_.toNumber(term)) &&
          _.map(this.usedCurrencies, currency => currency["code"]).includes(
            code
          ) &&
          priceErrorValue.length
        ) {
          data = this.addIssue(
            data,
            {
              billing_cycle_months: _.toNumber(term),
              currency_id: _.get(
                _.find(this.currencies, ["code", code]),
                "id",
                ""
              ),
              message: priceErrorValue[0],
              isError: true
            },
            errorPricelistId
          );
        }
      });
    });

    return _.sortBy(data, "priceListId");
  }

  public addIssue(
    data: IProductPricesIssueMessages[],
    issueMessage: IProductPricesIssueMessage,
    priceListId: IPricelist["id"]
  ) {
    const index = _.findIndex(data, ["priceListId", priceListId]);

    if (index > -1) {
      if (issueMessage.isError) {
        const errorIndex = _.findIndex(
          data[index].errors,
          currentError =>
            currentError.billing_cycle_months ===
              issueMessage.billing_cycle_months &&
            currentError.currency_id === issueMessage.currency_id
        );
        if (errorIndex > -1) {
          data[index].errors[errorIndex] = issueMessage;
        } else {
          data[index].errors.push(issueMessage);
        }
        data[index].warnings = _.filter(
          data[index].warnings,
          (warning: IProductPricesIssueMessage) => {
            return !(
              warning.billing_cycle_months ===
                issueMessage.billing_cycle_months &&
              warning.currency_id === issueMessage.currency_id
            );
          }
        );
      } else {
        data[index].warnings.push(issueMessage);
      }
    } else {
      data.push({
        priceListId: priceListId,
        warnings: issueMessage.isError ? [] : [issueMessage],
        errors: issueMessage.isError ? [issueMessage] : []
      });
    }

    return data;
  }

  public get canManagePrices() {
    const managePricesFunctionalities = [
      "create_pricelist_product_prices",
      "delete_pricelist_product_prices",
      "sync_pricelist_product_prices",
      "update_product"
    ];
    return this.$userCan(...managePricesFunctionalities);
  }

  // Computed

  public get formFieldsDisabled() {
    return !this.canManagePrices || this.isProcessing || this.isStaged;
  }

  // Guarding

  public guardFunctionalities = ["staff_get_product"];

  public get isContentAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }
}
